const DEFAULT_DATE_LOCALE = 'en-US'
const DATE_FORMAT_OPTIONS = Object.freeze({ month: 'short', day: 'numeric', year: 'numeric' })
const DATE_TIME_FORMAT_OPTIONS = Object.freeze({
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
})
const DEFAULT_DATE_FORMATTER = new Intl.DateTimeFormat(DEFAULT_DATE_LOCALE, DATE_FORMAT_OPTIONS)
const ISO_LOCAL_RE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2})(?::(\d{1,2})(?::(\d{1,2})(?:\.\d{1,3})?)?)?)?$/
const UNIX_TS_RE = /^-?\d+$/

/**
 * Parse API date strings safely without applying timezone offsets
 * for date-only or naive datetime formats (YYYY-MM-DD or YYYY-MM-DD HH:mm:ss).
 */
export function parseApiDate(input) {
  if (input == null || input === '') return null
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input

  const str = String(input).trim()
  if (!str) return null

  if (UNIX_TS_RE.test(str)) {
    const asNumber = Number(str)
    const millis = Math.abs(asNumber) < 1e12 ? asNumber * 1000 : asNumber
    const fromUnix = new Date(millis)
    return Number.isNaN(fromUnix.getTime()) ? null : fromUnix
  }

  const match = str.match(ISO_LOCAL_RE)
  if (match) {
    const [, y, m, d, hh = '0', mm = '0', ss = '0'] = match
    const localDate = new Date(
      Number(y),
      Math.max(0, Number(m) - 1),
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss)
    )
    return Number.isNaN(localDate.getTime()) ? null : localDate
  }

  const parsed = Date.parse(str.replace(/\s+/g, ' '))
  return Number.isNaN(parsed) ? null : new Date(parsed)
}

export function formatDisplayDate(input, config = {}) {
  const parsed = parseApiDate(input)
  if (!parsed) return ''
  const locale = Object.prototype.hasOwnProperty.call(config, 'locale')
    ? config.locale
    : DEFAULT_DATE_LOCALE
  const options = config.options
  if (!options && locale === DEFAULT_DATE_LOCALE) {
    return DEFAULT_DATE_FORMATTER.format(parsed)
  }
  return new Intl.DateTimeFormat(locale, options || DATE_FORMAT_OPTIONS).format(parsed)
}

export function formatDisplayDateTime(input, config = {}) {
  const parsed = parseApiDate(input)
  if (!parsed) return ''
  const locale = Object.prototype.hasOwnProperty.call(config, 'locale')
    ? config.locale
    : DEFAULT_DATE_LOCALE
  const options = config.options || DATE_TIME_FORMAT_OPTIONS
  return new Intl.DateTimeFormat(locale, options).format(parsed)
}

export function isSameCalendarDay(a, b) {
  const d1 = parseApiDate(a)
  const d2 = parseApiDate(b)
  if (!d1 || !d2) return false
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}

export function formatEventDateRange({
  fromDate,
  toDate,
  fromTime,
  toTime,
  rangeSeparator = ' - ',
  sameDaySeparator = ' - '
} = {}) {
  const startDate = formatDisplayDate(fromDate)
  const endDate = formatDisplayDate(toDate)

  const start = startDate
    ? `${startDate}${fromTime ? ` • ${fromTime}` : ''}`
    : (fromTime || '')
  const end = endDate
    ? `${endDate}${toTime ? ` • ${toTime}` : ''}`
    : (toTime || '')

  if (!endDate) {
    if (!start && end) return end
    if (start && toTime && !fromTime) {
      return `${start}${sameDaySeparator}${toTime}`
    }
    if (start && toTime && toTime !== fromTime && !end) {
      return `${start}${sameDaySeparator}${toTime}`
    }
    return start
  }

  const sameDay = isSameCalendarDay(fromDate, toDate)
  if (sameDay) {
    if (toTime && toTime !== fromTime) {
      return `${start}${sameDaySeparator}${toTime}`
    }
    return start || end
  }

  if (!start) return end
  if (!end) return start
  return `${start}${rangeSeparator}${end}`
}
